import { inject, Injectable } from '@angular/core';
import {
  FacilitySignIn,
  PublicFacility,
  RememberedFacilitySignIn,
  RememberedFacilitySignIns,
} from '../model/locations/facility-sign-in';
import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class FacilitySignInMemoryService {
  private readonly _storage = inject(StorageService);

  get(facility: PublicFacility): Promise<RememberedFacilitySignIns> {
    return this._storage.getFacilitySignIns(facility.facilityUid);
  }

  async remember(
    facility: PublicFacility,
    signIn: FacilitySignIn
  ): Promise<RememberedFacilitySignIn> {
    if (
      !signIn.id ||
      !signIn.name ||
      !signIn.signInTime ||
      Number.isNaN(signIn.signInTime.getTime())
    ) {
      return Promise.reject(
        new Error('A facility sign-in requires an ID, name, and timestamp.')
      );
    }

    const remembered: RememberedFacilitySignIn = {
      facilityUid: facility.facilityUid,
      facilityId: facility.id,
      signInId: signIn.id,
      name: signIn.name,
      signInTime: signIn.signInTime.toISOString(),
    };

    const signIns = await this._storage.getFacilitySignIns(
      facility.facilityUid
    );
    const nextSignIns = [
      ...signIns.filter((saved) => saved.signInId !== remembered.signInId),
      remembered,
    ];

    await this._storage.storeFacilitySignIns(facility.facilityUid, nextSignIns);
    return remembered;
  }

  async remove(
    facilityUid: string | null,
    facilityId: string,
    signInId: string
  ): Promise<void> {
    if (!facilityUid) {
      return Promise.resolve();
    }

    const signIns = await this._storage.getFacilitySignIns(facilityUid);
    const remaining = signIns.filter(
      (saved) => saved.facilityId !== facilityId || saved.signInId !== signInId
    );

    await this._storage.storeFacilitySignIns(facilityUid, remaining);
  }

  isEligibleForQuickSignOut(
    saved: RememberedFacilitySignIn | null,
    facility: PublicFacility
  ): boolean {
    if (
      !saved ||
      !saved.facilityUid ||
      !saved.facilityId ||
      saved.facilityId !== facility.id ||
      saved.facilityUid.toLowerCase() !== facility.facilityUid.toLowerCase() ||
      !saved.signInId ||
      !saved.signInTime
    ) {
      return false;
    }

    const signInDate = new Date(saved.signInTime);
    const today = new Date();
    return (
      !Number.isNaN(signInDate.getTime()) &&
      signInDate.getFullYear() === today.getFullYear() &&
      signInDate.getMonth() === today.getMonth() &&
      signInDate.getDate() === today.getDate()
    );
  }
}
